import type { TreeNode } from '../../render/tree';
import type { DemoKey } from '../../types';

export type VennPresetId =
  | 'dice-even-or-prime'
  | 'class-sports-2set'
  | 'class-sports-3set'
  | 'custom';

export interface VennPresetMeta {
  id: VennPresetId;
  labelKey: DemoKey;
  arity: 2 | 3;
  /** True when the preset is computed from `MASTER_SURVEY.sports_played`. */
  fromMasterSurvey?: boolean;
  /** True for the dice-even-or-prime / custom presets where universe is fixed/editable. */
  defaultUniverse: number;
}

export const VENN_PRESETS: readonly VennPresetMeta[] = [
  {
    id: 'dice-even-or-prime',
    labelKey: 'venn.dice-even-or-prime.label',
    arity: 2,
    defaultUniverse: 6,
  },
  {
    id: 'class-sports-2set',
    labelKey: 'venn.class-sports-2set.label',
    arity: 2,
    fromMasterSurvey: true,
    defaultUniverse: 30,
  },
  {
    id: 'class-sports-3set',
    labelKey: 'venn.class-sports-3set.label',
    arity: 3,
    fromMasterSurvey: true,
    defaultUniverse: 30,
  },
  { id: 'custom', labelKey: 'venn.custom.label', arity: 2, defaultUniverse: 12 },
];

export type TreePresetId = 'coin-toss' | 'dice-roll' | 'draw-without-replacement' | 'custom';

export interface TreePresetMeta {
  id: TreePresetId;
  labelKey: DemoKey;
  /**
   * Static tree root. Absent for `coin-toss` / `dice-roll`, whose trees are
   * built from live state (`tosses` / `dice` + `condition`) via
   * `event-trees.ts`'s `buildCoinTree` / `buildPerDieTree` / `buildSumTree`;
   * absent for `custom` too, whose tree is built from the shared probability
   * vector via `event-trees.ts`'s `buildCustomTree`, reconciled in
   * `probability-diagrams.ts`.
   */
  root?: TreeNode<DemoKey>;
}

export const TREE_PRESETS: readonly TreePresetMeta[] = [
  {
    id: 'coin-toss',
    labelKey: 'tree.coin-toss.label',
  },
  {
    id: 'dice-roll',
    labelKey: 'tree.dice-roll.label',
  },
  {
    id: 'draw-without-replacement',
    labelKey: 'tree.draw-without-replacement.label',
    root: {
      labelKey: 'tree.draw.start',
      children: [
        {
          probability: 2 / 5,
          node: {
            labelKey: 'tree.draw.red',
            children: [
              { probability: 1 / 4, node: { labelKey: 'tree.draw.red' } },
              { probability: 3 / 4, node: { labelKey: 'tree.draw.blue' } },
            ],
          },
        },
        {
          probability: 3 / 5,
          node: {
            labelKey: 'tree.draw.blue',
            children: [
              { probability: 2 / 4, node: { labelKey: 'tree.draw.red' } },
              { probability: 2 / 4, node: { labelKey: 'tree.draw.blue' } },
            ],
          },
        },
      ],
    },
  },
  {
    id: 'custom',
    labelKey: 'tree.custom.label',
  },
];
